import React, { useEffect, useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
// #region Components
import Input from "shared/components/FormElements/Input";
import DatePicker from "shared/components/FormElements/DatePicker";
import NotFound from "shared/components/UIElements/NotFound";
import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import FormFooter from "shared/components/FormElements/FormFooter";
// #endregion Components

// #region Utils
import { VALIDATOR_REQUIRE } from "shared/util/validators";
// #endregion Utils

// #region Hooks
import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";
// #endregion Hooks

const EducationYearItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  const { t } = useTranslation();
  //standard code
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const itemType = "educationYear";
  const endPointUpdate = "educationyears";
  const endPointDelete = "educationyears";
  const endPointCreate = "educationyears";

  const { educationYears } = useContext(StoreContext);
  // #endregion Standard Interface functions

  const [formState, inputHandler] = useForm(
    {
      name: { value: "", isValid: true },
      yearStartDate: { value: "", isValid: true },
      yearEndingDate: { value: "", isValid: true },
    },
    true
  );

  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const retrievedItem = educationYears.find((item) => item.id === itemId);
      setLoadedItem(retrievedItem);
    } catch (err) {}
    setIsItemLoaded(true);
  }, [educationYears, itemId]);

  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    const endpoint = isNewItem
      ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
      : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

    const postType = isNewItem ? "POST" : "PATCH";

    const infoToBeSent = {
      name: formState.inputs.name.value,
      yearStartDate: formState.inputs.yearStartDate.value,
      yearEndingDate: formState.inputs.yearEndingDate.value,
    };

    try {
      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      if (err?.message) {
        window.toast.error(err.message);
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemType}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container" onSubmit={itemUpdateSubmitHandler}>
          <Input
            id="name"
            element="input"
            type="text"
            label="Eğitim Yılı"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="Eğitim yılı giriniz"
            initialValue={loadedItem?.name || ""}
            initialValid={isNewItem ? false : true}
            onInput={inputHandler}
          />

          <DatePicker
            id="yearStartDate"
            onInput={inputHandler}
            label="Eğitim Başlangıç Tarihi"
            initialValid={isNewItem ? false : true}
            validators={[VALIDATOR_REQUIRE()]}
            initialValue={loadedItem?.yearStartDate?.toString() || null}
          ></DatePicker>

          <DatePicker
            id="yearEndingDate"
            onInput={inputHandler}
            label="Eğitim Bitiş Tarihi"
            initialValid={isNewItem ? false : true}
            validators={[VALIDATOR_REQUIRE()]}
            initialValue={loadedItem?.yearEndingDate?.toString() || null}
          ></DatePicker>

          <FormFooter
            isNewItem={true} // Alwasy hide delete button
            onUpdate={itemUpdateSubmitHandler}
            onDelete={() => setShowConfirmModal(true)}
            isUpdating={isLoading}
            disabled={
              !formState.isValid ||
              isLoading ||
              !formState.inputs.yearStartDate.value ||
              !formState.inputs.yearEndingDate.value
            }
          />
        </form>
      )}
    </React.Fragment>
  );
};

export default EducationYearItem;
